import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Settings as SettingsIcon,
  ChevronRight,
  ChevronsUpDown,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { MiMarcaProductsTab } from "../components/MiMarcaProductsTab";
import { TiendaNubeStatus } from "../components/TiendaNubeStatus";
import { MiMarcaModelsTab } from "../components/MiMarcaModelsTab";
import { CompanyData } from "./config_sections/CompanyData";

const PLAN_LABELS: Record<string, string> = {
  gratis: "Gratis",
  emprendimiento: "Básico",
  negocio: "Pro",
  enterprise: "Ultra",
  enterprise1: "Ultra",
  enterprise2: "Ultra",
  enterprise3: "Ultra",
  enterprise_plus: "Empresa",
};

const ATLAS_LOGO = "/images/estudio-atlas-logo-solo.svg";

type TabKey = "productos" | "diseno" | "modelos";
const TABS: { key: TabKey; label: string }[] = [
  { key: "productos", label: "Mis productos" },
  { key: "diseno", label: "Mi diseño" },
  { key: "modelos", label: "Mis Modelos" },
];

const optimizedLogo = (url: string | null): string => {
  if (!url) return ATLAS_LOGO;
  return url.includes("supabase.co") ? `${url}?width=80&height=80` : url;
};

/**
 * Pantalla "Mi marca": arriba una card de marca (logo + usuario + marca + plan,
 * tap para cambiar de marca si hay varias) y los créditos restantes; abajo un
 * selector de tabs (Mis productos · Mi diseño · Mis Modelos).
 */
export const MiMarca = () => {
  const navigate = useNavigate();
  const {
    profile,
    brands,
    currentBrandId,
    activeBrand,
    switchBrand,
    switching,
    loading,
  } = useBrandProfile();

  const [tab, setTab] = useState<TabKey>("productos");
  const [showBrandModal, setShowBrandModal] = useState(false);
  // Error/success para el componente CompanyData (Mi diseño).
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const hasMultipleBrands = brands.length > 1;
  const brandLogo = optimizedLogo(activeBrand?.logo_url ?? profile?.company_logo ?? null);
  const brandName = activeBrand?.name ?? profile?.company_name ?? "";
  const planLabel = profile?.plan ? PLAN_LABELS[profile.plan] ?? profile.plan : null;

  const remaining =
    typeof profile?.imagenes_restantes === "number"
      ? profile.imagenes_restantes
      : 0;
  const max =
    typeof profile?.max_fotos_mes === "number" ? profile.max_fotos_mes : 0;
  const pct = max > 0 ? Math.min((Math.min(remaining, max) / max) * 100, 100) : 0;

  return (
    <div className="relative px-4 sm:px-8 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen">
      {/* Acciones del header (solo mobile): ícono de Tiendanube + Configuración.
          En desktop la config va por el sidebar y Tiendanube por la card de la
          columna izquierda. */}
      <div className="md:hidden absolute top-1 right-4 sm:right-8 flex items-center gap-0.5">
        <TiendaNubeStatus
          variant="icon"
          companyId={profile?.id_company ?? null}
          brandId={currentBrandId}
        />
        <button
          type="button"
          onClick={() => navigate("/configuracion")}
          aria-label="Configuración"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full text-brand-black/70 hover:bg-black/[0.04] active:opacity-60 transition-colors"
        >
          <SettingsIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Título mobile (desktop usa el header de abajo) */}
      <MobilePageTitle>Mi marca</MobilePageTitle>
      {/* Desktop header. En lg queda sticky arriba (con fondo) para seguir
          visible al scrollear el contenido. */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11 lg:mb-0 lg:h-auto lg:py-4 lg:sticky lg:top-0 lg:z-20 lg:bg-surface-content">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4 lg:mt-0">
          Mi marca
        </h1>
      </div>

      {/* Layout: 1 columna hasta lg; en lg+ dos columnas (izq: marca +
          créditos + tabs verticales / der: contenido del tab). Usamos lg y no
          md porque a md el sidebar + columna izquierda dejan el área derecha
          demasiado angosta. */}
      <div className="lg:grid lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        {/* ── Columna izquierda (fija al scrollear en desktop, debajo del
            título sticky) ── */}
        <div className="lg:sticky lg:top-[60px] lg:self-start">
          {/* Card de marca (logo + usuario + marca + plan) */}
          {loading && !profile ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-brand-black/30" />
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  // Con varias marcas, abrir el switcher; con una sola, ir a
                  // Configuración.
                  if (hasMultipleBrands) setShowBrandModal(true);
                  else navigate("/configuracion");
                }}
                className="w-full flex items-center gap-3 bg-white border border-black/[0.06] rounded-2xl p-3.5 text-left shadow-sm hover:border-brand-primary/30 cursor-pointer transition-colors"
              >
                <img
                  src={brandLogo}
                  alt=""
                  className="w-12 h-12 rounded-full object-contain bg-white border border-black/[0.06] shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = ATLAS_LOGO;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-semibold text-brand-black/90 truncate">
                    {profile?.name || "Usuario"}
                  </div>
                  {brandName && (
                    <div className="text-[13px] text-brand-black/55 truncate">
                      {brandName}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {planLabel && (
                    <span className="text-[12px] font-semibold leading-none px-2 py-1 rounded-md bg-black/[0.06] text-brand-black/65">
                      {planLabel}
                    </span>
                  )}
                  {hasMultipleBrands && (
                    <ChevronsUpDown className="w-4 h-4 text-brand-black/40" />
                  )}
                </div>
              </button>

              {/* Créditos restantes (con ícono de tokens) → planes */}
              <div className="mt-6 px-0.5">
                <button
                  type="button"
                  onClick={() => navigate("/subscription")}
                  className="w-full flex items-center justify-between gap-2 mb-2.5 active:opacity-60 transition-opacity"
                >
                  <span className="flex items-center gap-1.5">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-[14px] h-[14px] object-contain"
                    />
                    <span className="text-[13px] font-medium text-brand-black/80">
                      Créditos restantes
                    </span>
                  </span>
                  <span className="flex items-center gap-0.5 text-neutral-500">
                    <span className="text-[13px]">{remaining} créditos restantes</span>
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </button>
                <div className="h-[5px] bg-black/[0.08] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-primary transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Tabs horizontales (mobile / hasta lg) */}
          <div className="lg:hidden mt-8 flex border-b border-black/[0.08]">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex-1 pb-2.5 text-[13.5px] font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-brand-primary text-brand-primary"
                      : "border-transparent text-brand-black/50 hover:text-brand-black/80"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tabs como containers verticales (lg+) */}
          <div className="hidden lg:flex lg:flex-col gap-2 mt-8">
            {TABS.map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-[14px] font-medium transition-colors ${
                    isActive
                      ? "border-brand-primary/40 bg-brand-primary/[0.06] text-brand-primary"
                      : "border-black/[0.07] bg-white hover:bg-black/[0.02] text-brand-black/80"
                  }`}
                >
                  {t.label}
                  <ChevronRight
                    className={`w-4 h-4 transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
                  />
                </button>
              );
            })}
          </div>

          {/* Integración Tiendanube — desde md+ (tablet/desktop) va acá, separada
              de los tabs con una divisoria + rótulo. En mobile va como ícono en
              el header (arriba a la derecha). */}
          <div className="hidden md:block mt-6 pt-5 border-t border-black/[0.08]">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-black/35 mb-2 px-1">
              Integración
            </p>
            <TiendaNubeStatus
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
            />
          </div>
        </div>

        {/* ── Columna derecha: contenido del tab seleccionado ── */}
        <div className="mt-5 lg:mt-0">
          {/* Banners de error/success (para Mi diseño) */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[14px] text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[14px] text-green-700">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {tab === "productos" && (
            <MiMarcaProductsTab
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
            />
          )}
          {tab === "diseno" &&
            (profile?.id_company ? (
              <CompanyData
                profile={profile}
                setError={setError}
                setSuccess={setSuccess}
                embedded
              />
            ) : (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-brand-black/30" />
              </div>
            ))}
          {tab === "modelos" && (
            <MiMarcaModelsTab companyId={profile?.id_company ?? null} />
          )}
        </div>
      </div>

      {/* ── Modal de cambio de marca (cuando hay varias) ── */}
      {showBrandModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
            onClick={() => !switching && setShowBrandModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  Cambiar de marca
                </h3>
                <button
                  onClick={() => setShowBrandModal(false)}
                  disabled={switching}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {brands.map((b) => {
                  const isActive = b.id === currentBrandId;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={async () => {
                        await switchBrand(b.id);
                        setShowBrandModal(false);
                      }}
                      disabled={switching}
                      className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 transition-colors disabled:opacity-60"
                    >
                      <img
                        src={optimizedLogo(b.logo_url)}
                        alt=""
                        className="w-9 h-9 rounded-[7px] object-contain bg-white border border-neutral-200 shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = ATLAS_LOGO;
                        }}
                      />
                      <span className="flex-1 text-[14px] font-medium text-brand-black/90 truncate">
                        {b.name}
                      </span>
                      {isActive && (
                        <Check className="w-4 h-4 text-brand-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
